import { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import {
    FiPlus, FiChevronLeft, FiChevronRight, FiX,
    FiEdit2, FiTrash2, FiCalendar, FiList,
    FiAlertCircle, FiBookOpen, FiUsers, FiClock, FiStar,
} from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import eventService from "../../services/eventService";
import { academicYearService } from "../../services/yearService";
import ConfirmModal from "../../components/common/ConfirmModal";

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const EVENT_TYPES = ["Holiday", "Exam", "Event", "Meeting", "Other"];

const TYPE_CONFIG = {
    Holiday: { color: "#dc2626", bg: "#fee2e2", icon: FiAlertCircle, label: "Holiday" },
    Exam: { color: "#7c3aed", bg: "#ede9fe", icon: FiBookOpen, label: "Exam" },
    Event: { color: "#003b67", bg: "#e8f1f8", icon: FiStar, label: "Event" },
    Meeting: { color: "#d97706", bg: "#fef3c7", icon: FiUsers, label: "Meeting" },
    Other: { color: "#6b7280", bg: "#f3f4f6", icon: FiClock, label: "Other" },
};

const EMPTY_FORM = {
    title: "",
    type: "Event",
    date: "",
    endDate: "",
    description: "",
    academicYear: "",
    isHoliday: false,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toYMD = (y, m, d) =>
    `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

const parseYMD = (str) => {
    if (!str) return null;
    const [y, m, d] = str.split("-").map(Number);
    return { year: y, month: m - 1, day: d };
};

/** Returns true if dateStr falls within [startStr, endStr] inclusive */
const inRange = (dateStr, startStr, endStr) => {
    if (!endStr) return dateStr === startStr;
    return dateStr >= startStr && dateStr <= endStr;
};

const asArray = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.data)) return value.data;
    return [];
};

// ─── Event dot strip ─────────────────────────────────────────────────────────

function EventDots({ events, max = 3 }) {
    const visible = events.slice(0, max);
    const extra = events.length - max;
    return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 2, marginTop: 2 }}>
            {visible.map((ev) => {
                const tc = TYPE_CONFIG[ev.type] || TYPE_CONFIG.Other;
                return (
                    <div
                        key={ev._id}
                        style={{
                            fontSize: 10, fontWeight: 600,
                            background: tc.bg, color: tc.color,
                            padding: "1px 5px", borderRadius: 99,
                            maxWidth: 80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}
                        title={ev.title}
                    >
                        {ev.title}
                    </div>
                );
            })}
            {extra > 0 && (
                <div style={{ fontSize: 10, color: "var(--text-muted)", alignSelf: "center" }}>
                    +{extra}
                </div>
            )}
        </div>
    );
}

// ─── Event Form Modal ─────────────────────────────────────────────────────────

function EventFormModal({ initial, allYears, onSave, onClose, saving }) {
    const [form, setForm] = useState({ ...EMPTY_FORM, ...initial });

    const set = (field, value) =>
        setForm((p) => ({ ...p, [field]: value }));

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div
                className="modal"
                style={{ width: 460, maxHeight: "90vh", overflowY: "auto" }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                    <h3 style={{ margin: 0 }}>
                        {initial?._id ? "Edit Event" : "Add Event"}
                    </h3>
                    <button
                        onClick={onClose}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 4 }}
                    >
                        <FiX size={18} />
                    </button>
                </div>

                <div style={{ display: "grid", gap: "0.85rem" }}>
                    {/* Title */}
                    <div className="form-group">
                        <label className="form-label">Title <span style={{ color: "var(--danger)" }}>*</span></label>
                        <input
                            className="form-control"
                            value={form.title}
                            onChange={(e) => set("title", e.target.value)}
                            placeholder="e.g. Independence Day Holiday"
                            autoFocus
                        />
                    </div>

                    {/* Type */}
                    <div className="form-group">
                        <label className="form-label">Type</label>
                        <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                            {EVENT_TYPES.map((t) => {
                                const c = TYPE_CONFIG[t];
                                const active = form.type === t;
                                return (
                                    <button
                                        key={t}
                                        type="button"
                                        onClick={() => set("type", t)}
                                        style={{
                                            padding: "4px 12px", borderRadius: 99, fontSize: 12,
                                            fontWeight: 600, border: "none", cursor: "pointer",
                                            background: active ? c.bg : "#f3f4f6",
                                            color: active ? c.color : "#6b7280",
                                            outline: active ? `2px solid ${c.color}` : "none",
                                            outlineOffset: 1,
                                        }}
                                    >
                                        {t}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Dates */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
                        <div className="form-group">
                            <label className="form-label">Start Date <span style={{ color: "var(--danger)" }}>*</span></label>
                            <input
                                type="date"
                                className="form-control"
                                value={form.date}
                                onChange={(e) => set("date", e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">End Date <span style={{ fontSize: 10, color: "var(--text-muted)" }}>(optional)</span></label>
                            <input
                                type="date"
                                className="form-control"
                                value={form.endDate}
                                min={form.date}
                                onChange={(e) => set("endDate", e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Academic Year */}
                    <div className="form-group">
                        <label className="form-label">Academic Year <span style={{ fontSize: 10, color: "var(--text-muted)" }}>(leave blank for all years)</span></label>
                        <select
                            className="form-control"
                            value={form.academicYear}
                            onChange={(e) => set("academicYear", e.target.value)}
                        >
                            <option value="">— All years (global) —</option>
                            {allYears.map((y) => (
                                <option key={y._id} value={y._id}>
                                    {y.label}{y.isActive ? " ✓" : ""}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Description */}
                    <div className="form-group">
                        <label className="form-label">Description <span style={{ fontSize: 10, color: "var(--text-muted)" }}>(optional)</span></label>
                        <textarea
                            className="form-control"
                            rows={2}
                            value={form.description}
                            onChange={(e) => set("description", e.target.value)}
                            placeholder="Additional details about this event…"
                            style={{ resize: "vertical" }}
                        />
                    </div>

                    {/* Holiday toggle */}
                    <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: 13, cursor: "pointer" }}>
                        <input
                            type="checkbox"
                            checked={form.isHoliday}
                            onChange={(e) => set("isHoliday", e.target.checked)}
                        />
                        Mark as holiday (no classes — attendance warning)
                    </label>
                </div>

                <div className="modal-actions" style={{ marginTop: "1.25rem" }}>
                    <button className="btn btn-outline" onClick={onClose}>Cancel</button>
                    <button
                        className="btn btn-primary"
                        onClick={() => onSave(form)}
                        disabled={saving || !form.title || !form.date}
                    >
                        {saving ? "Saving…" : initial?._id ? "Update Event" : "Add Event"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Day detail popover ───────────────────────────────────────────────────────

function DayPopover({ dateStr, events, onAdd, onEdit, onDelete, onClose }) {
    const parsed = parseYMD(dateStr);
    const label = parsed
        ? new Date(parsed.year, parsed.month, parsed.day)
            .toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
        : dateStr;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div
                className="modal"
                style={{ width: 400, maxHeight: "80vh", display: "flex", flexDirection: "column" }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: 15 }}>{label}</h3>
                        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                            {events.length} event{events.length !== 1 ? "s" : ""}
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: "0.4rem" }}>
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={() => onAdd(dateStr)}
                        >
                            <FiPlus size={12} /> Add
                        </button>
                        <button
                            onClick={onClose}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 4 }}
                        >
                            <FiX size={16} />
                        </button>
                    </div>
                </div>

                <div style={{ overflowY: "auto", flex: 1 }}>
                    {events.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "1.5rem", color: "var(--text-muted)", fontSize: 13 }}>
                            No events on this day.
                        </div>
                    ) : (
                        events.map((ev) => {
                            const tc = TYPE_CONFIG[ev.type] || TYPE_CONFIG.Other;
                            const Icon = tc.icon;
                            return (
                                <div
                                    key={ev._id}
                                    style={{
                                        display: "flex", alignItems: "flex-start", gap: "0.65rem",
                                        padding: "0.75rem", marginBottom: "0.5rem",
                                        background: tc.bg, borderRadius: 8,
                                        border: `1px solid ${tc.color}30`,
                                    }}
                                >
                                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                        <Icon size={13} color={tc.color} />
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 700, fontSize: 13, color: tc.color }}>{ev.title}</div>
                                        <div style={{ fontSize: 11.5, color: tc.color, opacity: 0.75, marginTop: 1 }}>
                                            {ev.type}
                                            {ev.endDate && ev.endDate !== ev.date && ` · until ${ev.endDate}`}
                                            {ev.isHoliday && " · 🏖 Holiday"}
                                        </div>
                                        {ev.description && (
                                            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4, lineHeight: 1.4 }}>
                                                {ev.description}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ display: "flex", gap: "0.25rem", flexShrink: 0 }}>
                                        <button
                                            style={{ background: "none", border: "none", cursor: "pointer", color: tc.color, padding: 4, opacity: 0.7 }}
                                            onClick={() => onEdit(ev)}
                                            title="Edit"
                                        >
                                            <FiEdit2 size={13} />
                                        </button>
                                        <button
                                            style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", padding: 4, opacity: 0.7 }}
                                            onClick={() => onDelete(ev)}
                                            title="Delete"
                                        >
                                            <FiTrash2 size={13} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── Main EventCalendar ───────────────────────────────────────────────────────

export default function EventCalendar() {
    const { academicYear } = useApp();

    const today = new Date();
    const [viewMonth, setViewMonth] = useState(today.getMonth());
    const [viewYear, setViewYear] = useState(today.getFullYear());
    const [viewMode, setViewMode] = useState("calendar"); // "calendar" | "list"

    const [events, setEvents] = useState([]);
    const [allYears, setAllYears] = useState([]);
    const [loading, setLoading] = useState(false);

    // Modal state
    const [formModal, setFormModal] = useState(null);  // null | { initial }
    const [dayPopover, setDayPopover] = useState(null);  // null | { dateStr, events }
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // Type filter
    const [typeFilter, setTypeFilter] = useState("");

    // ── Fetch events for visible month ────────────────────────────────────
    const fetchEvents = useCallback(async () => {
        setLoading(true);
        try {
            const res = await eventService.getAll({
                month: viewMonth + 1,
                year: viewYear,
                academicYear: academicYear?._id || "",
                type: typeFilter,
            });
            setEvents(asArray(res));
        } catch (err) {
            setEvents([]);
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    }, [viewMonth, viewYear, academicYear, typeFilter]);

    // ── Fetch all academic years for form dropdown ─────────────────────────
    useEffect(() => {
        academicYearService.getAll()
            .then((r) => setAllYears(asArray(r)))
            .catch(() => setAllYears([]));
    }, []);

    useEffect(() => { fetchEvents(); }, [fetchEvents]);

    // ── Navigation ────────────────────────────────────────────────────────
    const prevMonth = () => {
        if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
        else setViewMonth((m) => m - 1);
    };
    const nextMonth = () => {
        if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
        else setViewMonth((m) => m + 1);
    };
    const goToday = () => { setViewMonth(today.getMonth()); setViewYear(today.getFullYear()); };

    // ── Map events to dates ────────────────────────────────────────────────
    const eventsByDate = {};
    events.forEach((ev) => {
        // Expand multi-day events across all their days in the visible month
        const start = ev.date;
        const end = ev.endDate || ev.date;
        const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

        for (let d = 1; d <= daysInMonth; d++) {
            const dateStr = toYMD(viewYear, viewMonth, d);
            if (inRange(dateStr, start, end)) {
                if (!eventsByDate[dateStr]) eventsByDate[dateStr] = [];
                eventsByDate[dateStr].push(ev);
            }
        }
    });

    // ── Calendar grid cells ────────────────────────────────────────────────
    const firstDayOfMonth = new Date(viewYear, viewMonth, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const todayStr = toYMD(today.getFullYear(), today.getMonth(), today.getDate());

    const cells = [];
    // Leading blank cells
    for (let i = 0; i < firstDayOfMonth; i++) cells.push(null);
    // Day cells
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    // ── CRUD handlers ─────────────────────────────────────────────────────
    const handleSave = async (formData) => {
        setSaving(true);
        try {
            const payload = {
                ...formData,
                academicYear: formData.academicYear || null,
                endDate: formData.endDate || "",
            };
            if (formData._id) {
                const res = await eventService.update(formData._id, payload);
                toast.success(res.message);
            } else {
                const res = await eventService.create(payload);
                toast.success(res.message);
            }
            setFormModal(null);
            fetchEvents();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const res = await eventService.remove(deleteTarget._id);
            toast.success(res.message);
            setDeleteTarget(null);
            setDayPopover(null);
            fetchEvents();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setDeleting(false);
        }
    };

    const openDayPopover = (dateStr) => {
        const dayEvents = eventsByDate[dateStr] || [];
        setDayPopover({ dateStr, events: dayEvents });
    };

    const openAddForm = (dateStr) => {
        setDayPopover(null);
        setFormModal({ initial: { ...EMPTY_FORM, date: dateStr, academicYear: academicYear?._id || "" } });
    };

    const openEditForm = (ev) => {
        setDayPopover(null);
        setFormModal({ initial: { ...ev, academicYear: ev.academicYear?._id || ev.academicYear || "" } });
    };

    // Holiday count this month
    const holidayCount = events.filter((e) => e.isHoliday).length;
    const totalCount = events.length;

    return (
        <div>
            {/* ── Page header ── */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.75rem" }}>
                <div>
                    <h1 className="page-title">Event Calendar</h1>
                    <p className="page-subtitle">
                        {MONTHS[viewMonth]} {viewYear}
                        {totalCount > 0 && ` · ${totalCount} event${totalCount !== 1 ? "s" : ""}${holidayCount > 0 ? ` · ${holidayCount} holiday${holidayCount !== 1 ? "s" : ""}` : ""}`}
                    </p>
                </div>

                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                    {/* Type filter */}
                    <select
                        className="form-control"
                        style={{ width: 150, fontSize: 13 }}
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                    >
                        <option value="">All Types</option>
                        {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>

                    {/* View toggle */}
                    <div style={{ display: "flex", border: "1px solid var(--border)", borderRadius: "var(--radius)", overflow: "hidden" }}>
                        {[
                            { mode: "calendar", icon: FiCalendar },
                            { mode: "list", icon: FiList },
                        ].map(({ mode, icon: Icon }) => (
                            <button
                                key={mode}
                                type="button"
                                onClick={() => setViewMode(mode)}
                                style={{
                                    padding: "0.4rem 0.75rem",
                                    border: "none", cursor: "pointer",
                                    background: viewMode === mode ? "var(--primary)" : "transparent",
                                    color: viewMode === mode ? "#fff" : "var(--text-muted)",
                                    display: "flex", alignItems: "center",
                                }}
                                title={mode}
                            >
                                <Icon size={14} />
                            </button>
                        ))}
                    </div>

                    <button className="btn btn-outline" onClick={goToday} style={{ fontSize: 13 }}>Today</button>

                    <button
                        className="btn btn-primary"
                        onClick={() => setFormModal({ initial: { ...EMPTY_FORM, academicYear: academicYear?._id || "" } })}
                    >
                        <FiPlus size={14} /> Add Event
                    </button>
                </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: "1rem", alignItems: "start" }}>

                {/* ══ LEFT: Calendar / List ══ */}
                <div>
                    {/* Month nav */}
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.75rem" }}>
                        <button className="btn btn-outline btn-sm" onClick={prevMonth} style={{ padding: "0.35rem 0.6rem" }}>
                            <FiChevronLeft size={14} />
                        </button>
                        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, flex: 1 }}>
                            {MONTHS[viewMonth]} {viewYear}
                        </h2>
                        <button className="btn btn-outline btn-sm" onClick={nextMonth} style={{ padding: "0.35rem 0.6rem" }}>
                            <FiChevronRight size={14} />
                        </button>
                    </div>

                    {/* ── Calendar grid ── */}
                    {viewMode === "calendar" && (
                        <div className="card">
                            {loading && (
                                <div style={{ padding: "0.5rem 1rem", fontSize: 12, color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                                    Loading…
                                </div>
                            )}

                            {/* Day headers */}
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: "1px solid var(--border)" }}>
                                {WEEKDAYS.map((wd) => (
                                    <div
                                        key={wd}
                                        style={{
                                            padding: "0.5rem 0",
                                            textAlign: "center",
                                            fontSize: 11.5,
                                            fontWeight: 700,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.04em",
                                            color: wd === "Sun" ? "#dc2626" : "var(--text-muted)",
                                        }}
                                    >
                                        {wd}
                                    </div>
                                ))}
                            </div>

                            {/* Day cells */}
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
                                {cells.map((day, i) => {
                                    if (!day) {
                                        return <div key={`blank-${i}`} style={{ minHeight: 80, borderRight: "1px solid var(--border)", borderBottom: "1px solid var(--border)", background: "#fafafa" }} />;
                                    }

                                    const dateStr = toYMD(viewYear, viewMonth, day);
                                    const dayEvents = eventsByDate[dateStr] || [];
                                    const isToday = dateStr === todayStr;
                                    const isSunday = new Date(viewYear, viewMonth, day).getDay() === 0;
                                    const hasHoliday = dayEvents.some((e) => e.isHoliday);

                                    return (
                                        <div
                                            key={day}
                                            onClick={() => openDayPopover(dateStr)}
                                            style={{
                                                minHeight: 80,
                                                padding: "0.4rem 0.5rem",
                                                borderRight: "1px solid var(--border)",
                                                borderBottom: "1px solid var(--border)",
                                                cursor: "pointer",
                                                background: hasHoliday ? "#fff5f5" : isToday ? "#e8f1f8" : "#fff",
                                                transition: "background 0.1s",
                                                position: "relative",
                                            }}
                                            onMouseEnter={(e) => { if (!hasHoliday && !isToday) e.currentTarget.style.background = "#f8fafc"; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.background = hasHoliday ? "#fff5f5" : isToday ? "#e8f1f8" : "#fff"; }}
                                        >
                                            {/* Day number */}
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                <span
                                                    style={{
                                                        fontSize: 13,
                                                        fontWeight: isToday ? 800 : 500,
                                                        color: isToday ? "#003b67" : isSunday ? "#dc2626" : "var(--text)",
                                                        width: 24, height: 24,
                                                        borderRadius: "50%",
                                                        background: isToday ? "#e8f1f8" : "transparent",
                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                    }}
                                                >
                                                    {day}
                                                </span>
                                                {dayEvents.length > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); openAddForm(dateStr); }}
                                                        style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: 0, lineHeight: 1 }}
                                                        title="Add event"
                                                    >
                                                        <FiPlus size={12} />
                                                    </button>
                                                )}
                                            </div>

                                            {/* Event dots/pills */}
                                            <EventDots events={dayEvents} max={2} />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* ── List view ── */}
                    {viewMode === "list" && (
                        <div>
                            {loading ? (
                                <div className="loading-center"><div className="spinner" />Loading events…</div>
                            ) : events.length === 0 ? (
                                <div className="card">
                                    <div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "2.5rem" }}>
                                        <FiCalendar size={36} style={{ opacity: 0.2, display: "block", margin: "0 auto 0.75rem" }} />
                                        <p>No events in {MONTHS[viewMonth]} {viewYear}.</p>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ display: "grid", gap: "0.5rem" }}>
                                    {events.map((ev) => {
                                        const tc = TYPE_CONFIG[ev.type] || TYPE_CONFIG.Other;
                                        const Icon = tc.icon;
                                        return (
                                            <div
                                                key={ev._id}
                                                className="card"
                                                style={{
                                                    padding: "0.85rem 1.1rem",
                                                    borderLeft: `4px solid ${tc.color}`,
                                                    display: "flex", alignItems: "flex-start", gap: "0.75rem",
                                                }}
                                            >
                                                <div style={{ width: 32, height: 32, borderRadius: 9, background: tc.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                    <Icon size={14} color={tc.color} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontWeight: 700, fontSize: 14 }}>{ev.title}</div>
                                                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                                                        {ev.date}{ev.endDate && ev.endDate !== ev.date ? ` → ${ev.endDate}` : ""}
                                                        <span style={{ margin: "0 6px" }}>·</span>
                                                        <span style={{ color: tc.color, fontWeight: 600 }}>{ev.type}</span>
                                                        {ev.isHoliday && <span style={{ marginLeft: 6, fontSize: 11, background: "#fee2e2", color: "#dc2626", padding: "1px 6px", borderRadius: 99, fontWeight: 600 }}>Holiday</span>}
                                                        {ev.academicYear?.label && <span style={{ marginLeft: 6, color: "var(--text-muted)" }}>· {ev.academicYear.label}</span>}
                                                    </div>
                                                    {ev.description && (
                                                        <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 4 }}>{ev.description}</div>
                                                    )}
                                                </div>
                                                <div style={{ display: "flex", gap: "0.25rem", flexShrink: 0 }}>
                                                    <button className="btn btn-outline btn-sm" onClick={() => openEditForm(ev)} title="Edit"><FiEdit2 size={12} /></button>
                                                    <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(ev)} title="Delete"><FiTrash2 size={12} /></button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ══ RIGHT: Legend + Upcoming sidebar ══ */}
                <div style={{ display: "grid", gap: "1rem" }}>

                    {/* Legend */}
                    <div className="card">
                        <div className="card-header" style={{ fontSize: 13 }}>Event Types</div>
                        <div style={{ padding: "0.75rem 1rem", display: "grid", gap: "0.5rem" }}>
                            {EVENT_TYPES.map((t) => {
                                const tc = TYPE_CONFIG[t];
                                const Icon = tc.icon;
                                const count = events.filter((e) => e.type === t).length;
                                return (
                                    <div
                                        key={t}
                                        style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: 13, cursor: "pointer" }}
                                        onClick={() => setTypeFilter(typeFilter === t ? "" : t)}
                                    >
                                        <div style={{ width: 26, height: 26, borderRadius: 7, background: tc.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                            <Icon size={12} color={tc.color} />
                                        </div>
                                        <span style={{ flex: 1, color: typeFilter === t ? tc.color : "var(--text)", fontWeight: typeFilter === t ? 700 : 400 }}>{t}</span>
                                        {count > 0 && (
                                            <span style={{ fontSize: 11, background: tc.bg, color: tc.color, padding: "1px 7px", borderRadius: 99, fontWeight: 700 }}>{count}</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* This month summary */}
                    <div className="card">
                        <div className="card-header" style={{ fontSize: 13 }}>
                            {MONTHS[viewMonth]} Summary
                        </div>
                        <div style={{ padding: "0.75rem 1rem" }}>
                            {events.length === 0 ? (
                                <div style={{ fontSize: 13, color: "var(--text-muted)" }}>No events this month.</div>
                            ) : (
                                <div style={{ display: "grid", gap: "0.5rem" }}>
                                    {events.slice(0, 8).map((ev) => {
                                        const tc = TYPE_CONFIG[ev.type] || TYPE_CONFIG.Other;
                                        return (
                                            <div
                                                key={ev._id}
                                                style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start", padding: "0.4rem 0", borderBottom: "1px solid var(--border)", cursor: "pointer" }}
                                                onClick={() => openEditForm(ev)}
                                            >
                                                <div style={{ width: 3, height: 36, borderRadius: 2, background: tc.color, flexShrink: 0, marginTop: 2 }} />
                                                <div>
                                                    <div style={{ fontSize: 12.5, fontWeight: 600 }}>{ev.title}</div>
                                                    <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                                        {ev.date}{ev.endDate && ev.endDate !== ev.date ? ` → ${ev.endDate.slice(8)}` : ""}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {events.length > 8 && (
                                        <div style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "center" }}>+ {events.length - 8} more</div>
                                    )}
                                </div>
                            )}

                            <button
                                className="btn btn-outline btn-sm"
                                style={{ width: "100%", marginTop: "0.75rem", justifyContent: "center" }}
                                onClick={() => setFormModal({ initial: { ...EMPTY_FORM, academicYear: academicYear?._id || "" } })}
                            >
                                <FiPlus size={12} /> Add Event
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Modals ── */}
            {formModal && (
                <EventFormModal
                    initial={formModal.initial}
                    allYears={allYears}
                    onSave={handleSave}
                    onClose={() => setFormModal(null)}
                    saving={saving}
                />
            )}

            {dayPopover && (
                <DayPopover
                    dateStr={dayPopover.dateStr}
                    events={dayPopover.events}
                    onAdd={openAddForm}
                    onEdit={openEditForm}
                    onDelete={setDeleteTarget}
                    onClose={() => setDayPopover(null)}
                />
            )}

            {deleteTarget && (
                <ConfirmModal
                    title="Delete Event"
                    message={`Delete "${deleteTarget.title}"? This cannot be undone.`}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                    loading={deleting}
                />
            )}
        </div>
    );
}
